import { ReactNode } from 'react';
import styled from 'styled-components';
import { useDragAndDropContext } from './Context';
import { DragAndDropOrientation, DragAndDropSectionProps } from './types';
import { DragButton, DRAG_BUTTON_CLASS_NAME } from '@ardoq/icons';
import { getDropPosition, isTargetDragHandleButton } from './util';

const Wrapper = styled.div<{
  $dragButtonAlwaysVisible: boolean;
  $orientation: DragAndDropOrientation;
}>`
  display: flex;
  .${DRAG_BUTTON_CLASS_NAME} {
    line-height: ${props => (props.$orientation === 'horizontal' ? 0 : '20px')};
    display: ${props =>
      props.$orientation === 'horizontal' ? 'inline-flex' : 'block'};
    visibility: ${props =>
      props.$dragButtonAlwaysVisible ? 'visible' : 'hidden'};
  }
  &:hover {
    .${DRAG_BUTTON_CLASS_NAME} {
      visibility: visible;
    }
  }
`;

export const DragAndDropPositioningWrapper = styled.div<{
  $dragButtonPosition: 'left' | 'right';
}>`
  flex-grow: 1;
  order: ${props => (props.$dragButtonPosition === 'right' ? 0 : 1)};
`;

export const DragAndDropCardWrapper = ({
  dragAndDropCardId,
  children,
  className,
  dragButtonAlwaysVisible = false,
  dragButtonPosition = 'right',
  dataTestId,
  ...props
}: DragAndDropSectionProps & { children: ReactNode }) => {
  const { setDraggedCardId, setDraggedOverCard, resetState, orientation } =
    useDragAndDropContext();

  return (
    <Wrapper
      data-test-id={dataTestId}
      className={className}
      $dragButtonAlwaysVisible={dragButtonAlwaysVisible}
      $orientation={orientation}
      onDragStart={event => {
        // allow dragging only by drag handle button, but display entire card when dragged
        if (!isTargetDragHandleButton(event.target)) {
          return;
        }
        event.dataTransfer.setDragImage(event.currentTarget, 0, 0);
        setDraggedCardId(dragAndDropCardId || null);
      }}
      onDragOver={event => {
        const rect = event.currentTarget.getBoundingClientRect();
        setDraggedOverCard(
          dragAndDropCardId || null,
          getDropPosition(rect, event.clientX, event.clientY, orientation)
        );
      }}
      onDragEnd={() => {
        resetState();
      }}
      {...props}
    >
      <DragAndDropPositioningWrapper $dragButtonPosition={dragButtonPosition}>
        {children}
      </DragAndDropPositioningWrapper>
      {Boolean(dragAndDropCardId) && (
        <DragButton
          draggable={Boolean(dragAndDropCardId)}
          data-test-id={`${dataTestId}-drag-button`}
        />
      )}
    </Wrapper>
  );
};
